export type Guid = string;

export type Iso8601Date = string;

export type stringNullable = string | null | undefined;

export type StringIndexableDictionary<T> = {
  [key: string]: T;
};

export enum ApiRequestType {
  GET = "GET",
  POST = "POST",
  PUT = "PUT",
  PATCH = "PATCH",
  DELETE = "DELETE",
}

export enum HttpPatchOperationType {
  REPLACE = "REPLACE",
}

export enum Gender {
  Male = "Male",
  Female = "Female",
}

export enum SalesItemType {
  None = 0,
  Tax = 10,
  CourseSpecific = 100,
  SpecialArticle = 105,
  SystemArticle = 30,
  EnrollmentFee = 35,
  Item = 40,
  Vtm = 45,
  Other = 50,
  Insurance = 60,
  Travel = 70,
  Accommodation = 80,
  Deposit = 85,
  Course = 90,
}

export enum SalesItemSubType {
  NoSubtype = 1,
  AgentCommission = 2,
  Discount = 3,
  ExplorePack = 4,
  FunPack = 5,
  SportsOptions = 6,
  Transfer = 7,
  CaxInsurance = 8,
  EnrollmentFee = 9,
  HostFamily = 10,
  HostFamilyExtraNight = 11,
  Hotel = 12,
  NoAccommodation = 13,
  Other = 14,
  Residence = 15,
  ResidenceExtraNight = 16,
  OptionalExcursions = 17,
  PostTours = 18,
  HotelExtraNight = 19,
  ProtectionCoverage = 20,
  Activities = 21,
  Flight = 22,
  ReunionParty = 23,
  Meals = 24,
  TravelInsurance = 25,
  ActivityVouchers = 26,
  AcademicClasses = 27,
  Internship = 28,
  Books = 29,
  AccommodationServices = 30,
  Gadgets = 31,
  Scholarship = 32,
  VisaRelated = 33,
  UniversityCounselling = 34,
  TuitionDeposit = 35,
  AccommodationDeposit = 36,
  WorkAndTravel = 37,
  Item = 38,
  BoatTrainBus = 39,
  ExamExtension = 40,
  VtmCard = 41,
  ArrivalTransfer = 42,
  DepartureTransfer = 43,
  ArrivalAndDeparture = 44,
  Extension = 45,
  Termination = 46,
  LocalTransportation = 47,
  BalanceCleanup = 48,
  Refunds = 50,
  Course = 100,
}

export enum SalesItemCode {
  FTEFX = "FTEFX",
  FTEF = "FTEF",
  FTLO = "FTLO",
}

export enum ApplyType {
  None = 0,
  Mandatory = 1,
  Suggested = 2,
}

export enum UnitType {
  Unit = 0,
  Week = 1,
  Term = 2,
  Day = 3,
}

export enum LineItemCategory {
  Course = 0,
  SalesItem = 1,
  GroupCourse = 2,
}

export enum PriceType {
  FP = 0,
  EP = 1,
  FI = 2,
  NP = 3,
}

export enum CategoryType {
  PRW = 1,
}

export enum SegmentType {
  Default = 0,
  Course = 1,
  Break = 2,
}

export enum DurationUnit {
  Week = "Week",
  Day = "Day",
}

export enum PaymentStatus {
  ReservationSuccess = "ReservationSuccess",
  PaymentSuccess = "PaymentSuccess",
  PaymentProcessed = "PaymentProcessed",
}

export enum PaymentOption {
  FullPayment = "FullPayment",
  SplitPayment = "SplitPayment",
  ScholarshipFunding = "ScholarshipFunding",
  DownPayment = "DownPayment",
  FullPaymentWithDiscount = "FullPaymentWithDiscount",
  CustomAmountPayment = "CustomAmountPayment",
}

export enum PaymentPlan {
  Monthly = "MonthlyPayment",
  Installments = "InstallmentsPayment",
  Deferred = "DeferredPayment",
}

export type LineItem = {
  id: Guid;
  bundleId: Guid;
  code: string;
  courseType: string;
  sku: string;
  isBundled: boolean;
  type: SalesItemType;
  subType: SalesItemSubType;
  destination: string;
  departurePoint: stringNullable;
  description: string;
  englishDescription: string;
  additionalDescription: string;
  category: LineItemCategory;
  unitType: UnitType;
  order: number;
  startAt: Iso8601Date;
  endAt: Iso8601Date;
  price: Money;
  quantity: number;
  totalPrice: Money;
  isOptional: boolean;
  applyType: ApplyType;
  parentId: Guid;
  isPreparationCourse: boolean;
  priceType: PriceType;
  isDeleted: boolean;
  isPublic: boolean;
};

export type Duration = {
  value: number;
  unit: DurationUnit;
};

export type SegmentCourse = {
  code: string;
  name: string;
  price: Money;
};

export type SegmentGroupCourse = {
  code: string;
  id: string;
  sellingEntity: string;
  accommodationType: SalesItemSubType;
  hfArticleCode: string;
  residenceArticleCode: string;
};

export type Segment = {
  id: Guid;
  type: SegmentType;
  duration: Duration;
  destinationCode: string;
  primaryDestinationCode: string;
  departurePoint: stringNullable;
  course: SegmentCourse;
  group: SegmentGroupCourse | null;
  startAt: Iso8601Date;
  endAt: Iso8601Date;
  note: string;
  price: Price;
  category: CategoryType;
  courseCategory: stringNullable;
  lineItems: Array<LineItem>;
};

export type Money = {
  amount: number;
  currency: string;
};

export enum PaymentInfoStatus {
  Reserve = "Reserve",
  Paid = "Paid",
}

export enum Relationship {
  Father = "Father",
  Mother = "Mother",
  Child = "Child",
  Sibling = "Sibling",
  Guardian = "Guardian",
  SpouseCivilPartner = "Spouse/Civil partner",
  Other = "Other",
}

export type PaymentInfo = {
  payment: Money;
  status: PaymentInfoStatus;
  madeAt: Iso8601Date;
  /** From quotation API `PaymentType`; controls pay-in-full discount display on quote details */
  paymentType?: PaymentTypeEnum | null;
  /** From quotation API payment / payment-details when checkout applied a discount */
  appliedDiscount?: number | null;
};

export type Price = {
  brochure: string;
  season: string;
  totalPrice: Money;
  optionalPrice: Money;
};

export type QuoteCustomer = {
  firstName: string;
  lastName: string;
  gender: Gender | undefined;
  dob: string;
  countryOfResidence: string;
};

export type QuoteSalesUser = {
  salesOffice: string;
  userName: string;
  email: string;
};

export type Document = {
  isHideDatesInPDF: boolean;
  emailSubject: string;
  pdfName: string;
  pdfUrl: string;
  note: string;
  status: number;
};

export type Quote = {
  id: Guid;
  opportunityUuid: Guid;
  product: string;
  program: string;
  market: string;
  language: string;
  isPublic: boolean;
  isPreferredQuote?: boolean;
  createdAt: Iso8601Date;
  validUpTo: Iso8601Date;
  prices: Price;
  payments?: Array<PaymentInfo>;
  customer: QuoteCustomer;
  salesUser: QuoteSalesUser;
  document: Document;
  segments: Array<Segment>;
  status: QuotePaymentStatus;
};

export type InitiatePaymentRequest = {
  quoteExternalId: Guid;
  languageCode: string;
  description: string;
  termsVersion: string;
};

export enum PaymentTypeEnum {
  RECURRING_MONTHLY = "Recurring monthly payments",
  PAYMENT_PLAN = "Payment plan payments",
  SPONSORED_PAYMENT_PLAN = "Deferred payment plan",
  PAID_IN_FULL = "Paid in full",
  PAID_IN_FULL_WITH_DISCOUNT = "Paid in full with discount",
  RESERVED = "Reserved",
}

export type InitiateOraclePaymentRequest = {
  externalId: Guid;
  termsVersion: string;
  returnUrl: string;
  countryCode: string;
  paymentSource: string;
  efComPaymentSource: string;
  paymentReferenceAction: string;
  requestedPaymentAmount: number;
  locale: string;
  paymentType: PaymentTypeEnum;
  localCurrency?: string;
  localAmount?: number;
  exchangeRate?: number;
};

export type InitiateOraclePaymentSessionResponse = {
  sessionData: string;
  sessionId: string;
  correlationId: string;
  merchantReference: string;
};

export type ReservationRequest = {
  quoteExternalId: Guid;
  termsVersion: string;
};

export type ReservationResponse = {
  quoteId: Guid;
};

/** Response from GET /quote/installments?quoteId= (sales frontend endpoint) */
export type QuoteInstallmentsResponse = {
  quoteUUID: string;
  market: string;
  payInFullDiscount: boolean;
  installments: {
    numberOfInstallments: number;
    amount: number;
    currencyCode: string;
    firstInstallmentDueDate: string;
    lastInstallmentDueDate: string;
    items: Array<{ dueDate: string; amount: number }>;
  };
};

export type LocalPriceResponse = {
  totalPrice: number;
  reservationPrice: number;
  totalPriceWithDiscount: number;
  currency: string;
  appliedExchangeRate: number;
};

export type { CourseContent } from "@ilc-technology/martech-api-client";

export type CourseCategoryContent = {
  code: string;
  story: {
    content: {
      title: string;
      courseCode: string;
      lessonsPerWeek: string;
      lessonsPerWeekText: string;
      lessonsPerWeekDescription: string;
      description: string;
    };
  };
};

export type CourseCategories = {
  courses: Array<CourseCategoryContent>;
};

type PatchCourseRequestValue = {
  sku: string;
  quantity: number;
  startDate: string;
  endDate: string;
};

type PatchOptionalItemRequestValue = {
  isOptional: boolean;
};

export type PatchRequestBody = {
  op: HttpPatchOperationType;
  path: string;
  value: PatchCourseRequestValue | PatchOptionalItemRequestValue;
};

export type CourseIntensityUpgradeResponse = {
  newPrice: Money;
  sku: string;
};

export type DurationUpgradesQuoteDraftResponse = {
  durationOptions: Array<DurationUpgradeOptions>;
};

export type DurationUpgradeOptions = {
  duration: Duration;
  startDateAt: string;
  endDateAt: string;
  totalPrice: Money;
  optionalPrice: Money;
  difference: UpgradeDifference;
};

export type UpgradeDifference = {
  pricePerWeek: Money;
  duration: Duration;
  totalPrice: Money;
  optionalPrice: Money;
};

export enum EFComProgramCodes {
  LT = "lt",
  ILS = "ils",
  AYA = "aya",
  EXC = "exc",
}

export enum ProgramCodes {
  ILC = "ILC",
  ILS = "ILS",
}

export enum HostFamilyCodes {
  FMW = "FMW",
  FTW = "FTW",
  FSW = "FSW",
}

export type CurrencyInfo = {
  currencyCode: string;
  currencySymbol: string;
  displayPosition: string;
  noFractionalUnits?: boolean;
};

export enum QuotePaymentStatus {
  // status marking quotes as canceled when there is already one quote which is paid/reserved
  Canceled = "Canceled",
  Unreserved = "Unreserved",
  Reserved = "Reserved",
  Paid = "Paid",
}

export enum UpgradeCourseCode {
  Basic = "B",
  General = "G",
  Intensive = "I",
}

export type PaymentResult = {
  merchantReferenceId: string;
  paymentStatus: string;
  returnUrl: string;
  amount: number;
  currency: string;
};

export type OraclePaymentLibraryResult = {
  resultCode: string;
  sessionData: string;
  sessionResult: string;
};

export type OraclePaymentResult = {
  resultCode: string;
  sessionId: string;
  correlationId: string;
};

export type LanguageMapping = {
  [key: string]: string;
};

export type DestinationMapping = {
  [key: string]: string;
};

export class Payment {
  payment: Money;
  status: string;
  madeAt: string;
  paymentType?: PaymentTypeEnum | null;

  constructor(amount: number, currency: string, status: string, madeAt: string, paymentType?: PaymentTypeEnum | null) {
    this.payment = { amount, currency };
    this.status = status;
    this.madeAt = madeAt;
    if (paymentType !== undefined) {
      this.paymentType = paymentType;
    }
  }
}

export type ResponseWithFlowId = Response & { efFlowId: string };

export enum ErrorCode {
  Skip = "skip",
  NoError = "noError",
  NoQuotesLoaded = "noQuotesLoaded",
  IdentityPropertiesMisconfigured = "identityPropertiesMisconfigured",
  QuotesLoadingFailed = "quotesLoadingFailed",
  QuoteLoadingFailed = "quoteLoadingFailed",
  LoadedQuoteNotListed = "loadedQuoteNotListed",
  NoUpgradesLoaded = "noUpgradesLoaded",
  UpgradesLoadingFailed = "upgradesLoadingFailed",
  AuthenticationFailed = "authenticationFailed",
  NoTokenProvidedOrInvalidFormat = "noTokenProvidedOrInvalidFormat",
  QuoteUpgradeFailed = "quoteUpgradeFailed",
  QuoteSaveFailed = "quoteSaveFailed",
  QuoteDraftLoadingFailed = "quoteDraftLoadingFailed",
  ReservationPriceLoadingFailed = "reservationPriceLoadingFailed",
  FetchingInstallmentsFailed = "fetchingInstallmetnsFailed",
  DownloadingTermsFailed = "termsConditionsLoadingFailed",
  TermsForChosenProgramNotFound = "termsForSelectedProgramNotFound",
  TermsVersionForChosenProgramNotFound = "termsVersionForSelectedProgramNotFound",
  ReservationsPostFailed = "reservationsPostFailed",
  OracleSessionInitFailed = "oracleSessionInitFailed",
  PaymentsWithCorrelationFailedToFetch = "paymentsWithCorrelationFailedToFetch",
  CourseUpgradeFetchFailed = "courseUpgradeFetchFailed",
  SalesOfficesLoadFailed = "salesOfficesLoadFailed",
  SalesOfficesDataNotFound = "salesOfficesDataNotFound",
  AccountUpdateRequestFailed = "accountUpdateRequestFailed",
  RelatedContactFetchFailed = "relatedContactFetchFailed",
  RelatedContactCreationFailed = "relatedContactCreationFailed",
  RelatedContactUpdateFailed = "relatedContactUpdateFailed",
  TryingToPayAlreadyPaidQuote = "tryingToPayAlreadyPaidQuote",
  QuoteIsCancelled = "quoteIsCancelled",
  InitPaymentSessionFailed = "initPaymentSessionFailed",
  PaymentFailed = "paymentFailed",
  PaymentGatewayReturnedError = "paymentGatewayError",
  ReceivingPaymentsWasCancelled = "receivingPaymentsWasCancelled",
  ReceivingPaymentsError = "receivingPaymentsError",
  PrivacyPolicyForMarketNotFound = "privacyPolicyForMarketNotFound",
  MedicalDetailsUpdateFailed = "medicalDetailsUpdateFailed",
  MedicalDetailsFetchFailed = "medicalDetailsFetchFailed",
  CustomerDetailsFetchFailed = "customerDetailsFetchFailed",
  InstallmentsFetchFailed = "installmentsFetchFailed",
  DataUpdateFailed = "dataUpdateFailed",
  PassportDetailsFetchFailed = "passportDetailsFetchFailed",
  MarketDetailsFetchFailed = "marketDetailsFetchFailed",
}

export type OptionalErrorDetails = {
  fallbackMessage?: string;
  fallbackTitle?: string;
  additionalDetails?: string;
};

/*
  Code must be provided - it's shown to the user and decides on the title and message.
  Provide customTitle/customMessage to skip using the deducted title and message from the error code itself.
  AdditionalDetails are shown to the user in the technical (less visible section).
  rawResponse - provide the raw response from the server to allow this class to create a generic details message that can
  be shown together with the error.
 */
class ErrorDetails {
  public code: ErrorCode;
  public details?: OptionalErrorDetails;
  public responseStatusCode?: number;
  public responseBody?: string;
  public efFlowId?: string;
  public url?: string;
  public hideAdditionalDetails?: boolean;

  constructor({
    code,
    details,
    responseStatusCode,
    responseBody,
    efFlowId,
    url,
    hideAdditionalDetails,
  }: Partial<ErrorDetails> = {}) {
    // Set additional properties specific to ErrorDetails
    this.code = code || ErrorCode.NoError;
    this.details = details;
    this.responseStatusCode = responseStatusCode;
    this.responseBody = responseBody;
    this.efFlowId = efFlowId;
    this.url = url;
    this.hideAdditionalDetails = hideAdditionalDetails;

    // Ensure that the prototype chain is correct
    Object.setPrototypeOf(this, ErrorDetails.prototype);
  }
}

// Expose the ErrorDetails class
export { ErrorDetails };
